import { useContext, useEffect } from 'react';
import { FeatureFlagContext } from '@contexts/featureFlag/featureFlag-context';
import { LoaderContext } from '@contexts/loader/loader-context';
import { QueryFunction, QueryKey, useQuery, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import { computeQueryEnabled } from '@utils/auth/compute-query-enabled';
import { RequestError } from '@utils/request/request.type';
import { showToastErrorRequest } from '@utils/show-toast-error-request/show-toast-error-request';

import { useRefreshTokenHandler } from './auth/use-refresh-token-handler';
import { useTokenManagement } from './auth/use-token-management';
import { useToast } from './use-toast/use-toast';

export const useGenericQuery = <
  TQueryFnData = unknown,
  TError = RequestError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  key: TQueryKey,
  fn: QueryFunction<TQueryFnData, TQueryKey>,
  options?: Omit<UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>, 'queryFn' | 'queryKey'>,
  handleLoading?: boolean,
  hideError?: boolean
): UseQueryResult<TData, TError> => {
  const { decrementLoaderQueue, incrementLoaderQueue } = useContext(LoaderContext);
  const { isSelectVenueActive, isPublicEventReportPage } = useContext(FeatureFlagContext);
  const toast = useToast();
  const requestId = `query-${key}`;

  const { getTokens } = useTokenManagement();
  const { accessToken, refreshToken } = getTokens();

  const { createRefreshAndRetryHandler } = useRefreshTokenHandler();
  const handleRefreshAndRetry = createRefreshAndRetryHandler<
    Parameters<QueryFunction<TQueryFnData, TQueryKey>>[0],
    TQueryFnData | Promise<TQueryFnData>
  >((context) => fn(context));

  // QUERY FUNCTION WITH IMMEDIATE RETRY ON 401
  const queryFnWithRetry: QueryFunction<TQueryFnData, TQueryKey> = async (context) => {
    try {
      return await fn(context);
    } catch (error: unknown) {
      if (error instanceof RequestError && error.status === 401) {
        return await handleRefreshAndRetry(context);
      }
      // If not 401, propagate the error
      throw error;
    }
  };

  // Determine if query should be enabled
  const finalEnabled = computeQueryEnabled({
    isPublicEventReportPage,
    accessToken,
    refreshToken,
    optionsEnabled: options?.enabled,
  });

  const query = useQuery(key, queryFnWithRetry, {
    ...options,
    enabled: finalEnabled,
    onSuccess: (data) => {
      if (options?.onSuccess) options.onSuccess(data);
      decrementLoaderQueue(requestId);
    },
    onError: (error) => {
      if (options?.onError) options.onError(error);
      if (error instanceof RequestError) {
        if (!hideError && showToastErrorRequest(error)) {
          toast.push({
            title: error.message,
            text: '',
            type: 'error',
            disableTimeOut: true,
          });
        }
      }
      decrementLoaderQueue(requestId);
    },
    onSettled: (data, error) => {
      if (options?.onSettled) options.onSettled(data, error);
      decrementLoaderQueue(requestId);
    },
    retry: (failureCount, error) => {
      // Don't retry on 401 - already handled by queryFnWithRetry
      if (error instanceof RequestError && error.status === 401) {
        return false;
      }
      if (isSelectVenueActive && error instanceof RequestError) {
        return false;
      }
      if (options?.retry !== false) {
        const numberOfRetry = options?.retry && typeof options?.retry === 'number' ? options?.retry : 4;
        return failureCount < numberOfRetry;
      }
      return false;
    },
  });

  useEffect(() => {
    if (query.isInitialLoading) {
      incrementLoaderQueue(requestId);
    }
    return () => {
      if (query.isInitialLoading) {
        decrementLoaderQueue(requestId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.isInitialLoading, handleLoading]);

  return query;
};
